import { motion } from "framer-motion";
import { Clipboard, MessageCircle, MessageSquare, Send } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "../../lib/api";
import { formatDateTime } from "../../lib/format";


const CHANNEL_META = {
    sms: { Icon: MessageSquare, label: "SMS", color: "bg-shariti-orange" },
    whatsapp: { Icon: MessageCircle, label: "WhatsApp", color: "bg-emerald-500" },
    email: { Icon: Send, label: "Email", color: "bg-shariti-black" },
};

const STATUS_META = {
    generated: { label: "Généré", color: "text-slate-500" },
    copied: { label: "Copié", color: "text-shariti-black" },
    sent_externally: { label: "Envoyé", color: "text-emerald-600" },
    queued: { label: "En file", color: "text-amber-600" },
    sent: { label: "Envoyé", color: "text-emerald-600" },
    delivered: { label: "Livré", color: "text-emerald-700" },
    read: { label: "Lu", color: "text-emerald-700" },
    failed: { label: "Échec", color: "text-shariti-red" },
};


export default function MessageHistory({ candidateId, refreshTick }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const { data } = await api.get(`/candidates/${candidateId}/messages`);
                if (!cancelled) setItems(data || []);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [candidateId, refreshTick]);

    if (loading) {
        return <div className="text-xs font-medium text-slate-400 italic">Chargement de l'historique…</div>;
    }

    if (items.length === 0) {
        return (
            <div className="text-xs font-medium text-slate-400 italic" data-testid="message-history-empty">
                Aucun message envoyé pour l'instant.
            </div>
        );
    }

    return (
        <ul className="space-y-2" data-testid="message-history-list">
            {items.map((m) => {
                const ch = CHANNEL_META[m.channel] || CHANNEL_META.sms;
                const st = STATUS_META[m.status] || STATUS_META.generated;
                const isOpen = !!expanded[m.id];
                return (
                    <motion.li
                        key={m.id}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-2xl bg-shariti-cream/60 border border-shariti-gray p-3"
                    >
                        <button
                            type="button"
                            onClick={() => setExpanded((e) => ({ ...e, [m.id]: !e[m.id] }))}
                            className="w-full flex items-start gap-3 text-left"
                            data-testid={`message-history-item-${m.id}`}
                        >
                            <div className={`h-8 w-8 rounded-xl ${ch.color} text-white flex items-center justify-center shrink-0`}>
                                <ch.Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-extrabold text-shariti-black">
                                        {m.template_name || "Message personnalisé"}
                                    </span>
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                        · {ch.label}
                                    </span>
                                    <span className={`text-[10px] font-bold uppercase tracking-widest ${st.color}`}>
                                        · {st.label}
                                    </span>
                                </div>
                                <div className="text-[11px] font-medium text-slate-500 mt-0.5">
                                    {m.sent_by_name || "—"} · {formatDateTime(m.sent_at)}
                                </div>
                                {!isOpen && m.body && (
                                    <div className="text-[11px] font-medium text-slate-600 mt-1 line-clamp-1">
                                        {m.body.slice(0, 100).replace(/\n/g, " ")}…
                                    </div>
                                )}
                            </div>
                            <Clipboard className={`h-3.5 w-3.5 text-slate-300 shrink-0 transition ${isOpen ? "rotate-90 text-shariti-orange" : ""}`} />
                        </button>
                        {isOpen && (
                            <pre className="mt-3 text-[11px] font-medium text-slate-700 bg-white rounded-xl border border-shariti-gray p-3 whitespace-pre-wrap font-mono leading-relaxed">
                                {m.body}
                            </pre>
                        )}
                    </motion.li>
                );
            })}
        </ul>
    );
}
